import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

// Admin: AI chat history viewer. Lists chat owners (logged-in members + anonymous
// visitors keyed by IP) with an activity summary, and shows the full transcript for
// a selected owner. Read-only. Admin-gated server-side (/api/admin/*).

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');

const AdminChatHistory = ({ embedded = false }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // {owner_type, owner_key, label}
  const [transcript, setTranscript] = useState([]);
  const [tLoading, setTLoading] = useState(false);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/admin/ai-chat/users');
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load chat users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openTranscript = async (u) => {
    const label = u.member_email || u.owner_key;
    setSelected({ owner_type: u.owner_type, owner_key: u.owner_key, label });
    setTLoading(true);
    setTranscript([]);
    try {
      const res = await axios.get('/admin/ai-chat/transcript', {
        params: { ownerType: u.owner_type, ownerKey: u.owner_key },
      });
      setTranscript(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error('Failed to load transcript');
    } finally {
      setTLoading(false);
    }
  };

  const filtered = users.filter((u) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (u.owner_key || '').toLowerCase().includes(q) ||
      (u.member_email || '').toLowerCase().includes(q) ||
      (u.owner_type || '').toLowerCase().includes(q);
  });

  const content = (
    <>
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Users list */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-3 border-b border-gray-100">
            <input
              type="text"
              placeholder="Search by email, IP, or type…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading…</div>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-600 sticky top-0">
                    <tr>
                      <th className="px-3 py-2">User</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">Msgs</th>
                      <th className="px-3 py-2">Tokens</th>
                      <th className="px-3 py-2">Last</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((u) => {
                      const label = u.member_email || u.owner_key;
                      const sel = selected && selected.owner_type === u.owner_type && selected.owner_key === u.owner_key;
                      return (
                        <tr key={`${u.owner_type}:${u.owner_key}`}
                            className={`border-t border-gray-100 cursor-pointer hover:bg-gray-50 ${sel ? 'bg-blue-50' : ''}`}
                            onClick={() => openTranscript(u)}>
                          <td className="px-3 py-2 font-medium text-blue-600">{label}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs ${u.owner_type === 'MEMBER' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>
                              {u.owner_type === 'MEMBER' ? 'member' : 'anon'}
                            </span>
                          </td>
                          <td className="px-3 py-2">{u.messages} <span className="text-gray-400">({u.user_msgs} Q)</span></td>
                          <td className="px-3 py-2 text-gray-500">{(u.tokens_in || 0) + (u.tokens_out || 0)}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-500">{fmtDate(u.last_msg)}</td>
                        </tr>
                      );
                    })}
                    {filtered.length === 0 && (
                      <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-500">No chat users.</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Transcript */}
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-3 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">
                {selected ? `Transcript — ${selected.label}` : 'Select a user to view the transcript'}
              </h3>
            </div>
            <div className="p-4 max-h-[70vh] overflow-y-auto space-y-3">
              {tLoading ? (
                <div className="text-center text-gray-500 py-8">Loading transcript…</div>
              ) : !selected ? (
                <div className="text-center text-gray-400 py-8">No user selected.</div>
              ) : transcript.length === 0 ? (
                <div className="text-center text-gray-500 py-8">No messages.</div>
              ) : (
                transcript.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                      m.role === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900'
                    }`}>
                      <div className={`text-[10px] mb-1 ${m.role === 'user' ? 'text-blue-100' : 'text-gray-400'}`}>
                        {m.role} · {fmtDate(m.created_at)}
                      </div>
                      {m.content && m.content.trim()
                        ? m.content
                        : <span className="italic opacity-60">(empty response)</span>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-3">Read-only. Transcripts contain user questions and AI answers.</p>
    </>
  );

  if (embedded) {
    return (
      <div>
        <div className="flex justify-end mb-3">
          <button onClick={load} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded">Refresh</button>
        </div>
        {content}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AI Chat History</h1>
            <p className="text-sm text-gray-500">Ask-AI transcripts by user (members &amp; anonymous)</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin" className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50">← Admin</Link>
            <button onClick={load} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded">Refresh</button>
          </div>
        </div>
        {content}
      </div>
    </div>
  );
};

export default AdminChatHistory;
